<div class="page">
  <!-- Loading skeleton -->
  <div class="loading" *ngIf="loading">
    <div class="card skel-card">
      <div class="skel-row">
        <div class="skel circle"></div>
        <div class="skel-col">
          <div class="skel line w70"></div>
          <div class="skel line w40"></div>
        </div>
      </div>
      <div class="skel line w160"></div>
      <div class="skel chips">
        <div class="skel chip"></div>
        <div class="skel chip"></div>
        <div class="skel chip"></div>
      </div>
    </div>
    <div class="skel block"></div>
  </div>

  <!-- No slug / invalid link: nothing to load, nothing to retry -->
  <div class="no-page" *ngIf="!loading && noSlug">
    <div class="no-page-icon">🔗</div>
    <h2>Nothing to show here</h2>
    <p>This link looks incomplete. Please open the review link shared with you.</p>
  </div>

  <!-- Error: had a slug but the load failed -->
  <div class="error" *ngIf="!loading && loadError && !noSlug">
    <div class="error-icon">⚠️</div>
    <p>Could not load review details. Please try again.</p>
  </div>

  <!-- Sentiment gate: 👍 opens the AI review screen, 👎 the feedback form -->
  <app-thumbs-prompt
    *ngIf="!loading && !loadError && config && step === 'thumbs'"
    [businessName]="businessTitle"
    [businessLogo]="config.business_logo"
    [contactName]="config.contact_name"
    [welcomeMessage]="welcomeMessage"
    [busy]="thumbsBusy"
    (picked)="pickThumbs($event)"
  ></app-thumbs-prompt>

  <app-negative-feedback
    *ngIf="!loading && !loadError && config && step === 'negative'"
    [businessName]="businessTitle"
    [slug]="slug_value"
    (submitted)="onNegativeSubmitted()"
    (alreadyGiven)="onFeedbackAlreadyGiven()"
    (back)="onNegativeBack()"
  ></app-negative-feedback>

  <!-- Terminal thank-you after negative feedback -->
  <div class="card thanks-card" *ngIf="step === 'thanks'">
    <div class="thanks-emoji">🙏</div>
    <h2 *ngIf="!feedbackAlreadyGiven">Thank you for your feedback!</h2>
    <h2 *ngIf="feedbackAlreadyGiven">Feedback already received</h2>
    <p *ngIf="!feedbackAlreadyGiven">
      We're sorry your visit fell short. Your comments go straight to the team.
    </p>
    <p *ngIf="feedbackAlreadyGiven">
      We've already recorded your feedback for this visit. Thank you.
    </p>
  </div>

  <!-- Main -->
  <div class="content" *ngIf="!loading && !loadError && config && step === 'review'">
    <div class="card hero" [class.collapsed]="heroCollapsed">
      <div class="biz-header">
        <div class="biz-logo-box">
          <img
            *ngIf="config.business_logo"
            class="biz-logo"
            [src]="config.business_logo"
            [alt]="businessTitle"
          />
          <span class="biz-logo-placeholder" *ngIf="!config.business_logo">
            {{ logoInitial }}
          </span>
        </div>
        <div class="biz-meta">
          <div class="biz-name">{{ businessTitle }}</div>
          <div class="biz-outlet" *ngIf="outletLine">{{ outletLine }}</div>
          <div class="biz-location" *ngIf="config.location">
            📍 {{ config.location }}
          </div>
        </div>
      </div>

      <div class="welcome">
        <span class="wave">👋</span>
        <p>{{ welcomeMessage }}</p>
      </div>

      <!-- FULL AI flow: highlight topics (intents are picked internally) -->
      <ng-container *ngIf="mode === 'full' && keywordChips.length">
        <div class="section-label">✨ What would you like to highlight?</div>
        <p class="section-hint">{{ highlightHint }}</p>
        <div class="chip-wrap">
          <button
            type="button"
            class="chip chip-ai"
            *ngFor="let kw of keywordChips"
            [class.selected]="isKeywordSelected(kw)"
            (click)="toggleKeyword(kw)"
          >
            {{ kw }}
          </button>
        </div>
      </ng-container>
    </div>

    <div class="review-wrap" [class.full]="mode === 'full'">
      <div class="review-head">
        <svg class="ico" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
        Your review

        <!-- AI assist indication — full mode only (minimal has the Polish chip) -->
        <span class="ai-badge" *ngIf="mode === 'full'">
          <svg class="ico sm" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 4l1.6 6.4L20 12l-6.4 1.6L12 20l-1.6-6.4L4 12l6.4-1.6z" />
          </svg>
          AI assist
        </span>
      </div>

      <!-- One-time hint: the draft was auto-generated from a seeded selection -->
      <div
        class="draft-hint"
        *ngIf="mode === 'full' && showInitialDraftHint && !isGenerating"
      >
        <svg class="ico sm" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 4l1.6 6.4L20 12l-6.4 1.6L12 20l-1.6-6.4L4 12l6.4-1.6z" />
        </svg>
        <span class="draft-hint-text">
          We drafted a review for you — feel free to edit it or reselect your highlights.
        </span>
        <button
          type="button"
          class="draft-hint-close"
          aria-label="Dismiss"
          (click)="dismissInitialDraftHint()"
        >
          <svg class="ico sm" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="textarea-box">
        <textarea
          #reviewArea
          class="review-textarea"
          [(ngModel)]="reviewDraft"
          (input)="autosize(); dismissInitialDraftHint()"
          [placeholder]="
            mode === 'full'
              ? 'Your review appears here…'
              : 'Share what made your visit great…'
          "
        ></textarea>

        <!-- Subtle loader while the AI works (generate / refine / polish) -->
        <div class="ta-loader" *ngIf="isGenerating || isRefining || isPolishing">
          <span class="ta-spinner"></span>
          <span class="ta-loader-text">
            {{
              isGenerating
                ? "Writing your review…"
                : isPolishing
                ? "Polishing…"
                : "Updating…"
            }}
          </span>
        </div>

        <!-- Prominent overlay: highlights changed after a draft exists → regenerate -->
        <div
          class="regen-overlay"
          *ngIf="mode === 'full' && selectionChangedSinceGenerate && reviewDraft.trim() && !isGenerating"
        >
          <span class="regen-overlay-dot"></span>
          <p class="regen-overlay-text">
            Your highlights changed — update the review to match.
          </p>
          <div class="regen-overlay-actions">
            <button
              type="button"
              class="regen-overlay-cancel"
              (click)="dismissRegenNudge()"
            >
              Keep as is
            </button>
            <button
              type="button"
              class="regen-overlay-btn"
              [disabled]="isGenerating"
              (click)="generateNew()"
            >
              <svg
                class="ico sm"
                [class.spin]="isGenerating"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Update review
            </button>
          </div>
        </div>
      </div>

      <!-- FULL AI: Regenerate below the textarea -->
      <div class="tool-row" *ngIf="mode === 'full'">
        <button
          class="tool-btn"
          [disabled]="isGenerating"
          (click)="generateNew()"
        >
          <svg
            class="ico sm"
            [class.spin]="isGenerating"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Regenerate
        </button>
      </div>

      <!-- MINIMAL AI: in-field polish row, below the text -->
      <div class="infield-tools" *ngIf="mode === 'minimal'">
        <span class="polish-left" *ngIf="polishesLeft <= 1">
          {{ polishesLeft }} polish left
        </span>
        <button
          class="tool-btn polish"
          [disabled]="isPolishing || polishesLeft <= 0"
          (click)="polish()"
        >
          <svg
            class="ico sm"
            [class.spin]="isPolishing"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M12 4l1.6 6.4L20 12l-6.4 1.6L12 20l-1.6-6.4L4 12l6.4-1.6z" />
          </svg>
          {{ isPolishing ? "Polishing…" : polishesLeft <= 0 ? "No polishes left" : "Polish" }}
        </button>
      </div>

      <!-- MINIMAL: tappable starter phrases (max 3, two short rows) -->
      <div class="hint-row" *ngIf="mode === 'minimal'">
        <span class="hint-label">Quick add</span>
        <button
          type="button"
          class="hint-chip"
          *ngFor="let h of promptHints"
          (click)="addHint(h)"
        >
          + {{ h }}
        </button>
      </div>

      <div class="review-meta">
        <span>You can edit this before posting</span>
        <span>{{ charCount }} chars</span>
      </div>
    </div>

    <div class="actions">
      <button class="btn-primary" (click)="copyAndPostToGoogle()">
        <svg class="ico" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
        Copy &amp; Post to Google
      </button>

      <!-- How-to-post instructions, shown once a review is ready (before copy) -->
      <div
        class="post-instructions"
        *ngIf="!reviewCopied && !copyFailed && mode === 'full' && reviewDraft.trim()"
      >
        <p class="post-steps-title">
          <svg class="info-ico" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="11" x2="12" y2="16" />
            <circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" />
          </svg>
          After tapping above, just two quick steps on Google:
        </p>
        <ol class="post-steps-list">
          <li>Tap the review box and <strong>paste</strong> your review.</li>
          <li>Tap <strong>Post</strong> to publish it.</li>
        </ol>
      </div>

      <!-- Point 3 — intermediate manual-copy screen. Only appears when the
           automatic copy failed (e.g. WhatsApp WebView). Review stays visible
           above; user long-presses to copy, then opens Google separately. -->
      <div class="post-instructions post-manual" *ngIf="copyFailed">
        <p class="post-steps-title">
          <svg class="info-ico" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="11" x2="12" y2="16" />
            <circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" />
          </svg>
          Couldn't copy automatically. Copy it by hand:
        </p>
        <ol class="post-steps-list">
          <li><strong>Long-press</strong> the review above → <strong>Select all</strong> → <strong>Copy</strong>.</li>
          <li>Then tap the button below to open Google and <strong>paste</strong> it.</li>
        </ol>
        <button class="btn-secondary open-google-btn" (click)="openGoogleAfterManualCopy()">
          I've copied it — Open Google
        </button>
      </div>

      <!-- Thank-you confirmation shown after a CONFIRMED copy -->
      <div class="post-instructions post-done" *ngIf="reviewCopied">
        <p class="post-thanks">🎉 Thank you! Your review is copied.</p>
        <p class="post-steps-title">
          <svg class="info-ico" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="11" x2="12" y2="16" />
            <circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" />
          </svg>
          On the Google page:
        </p>
        <ol class="post-steps-list">
          <li>Tap the review box and <strong>paste</strong> your review.</li>
          <li>Tap <strong>Post</strong> to publish it.</li>
        </ol>
      </div>

      <p class="footnote" *ngIf="!reviewCopied && !copyFailed">
        Takes just a few seconds
      </p>
    </div>
  </div>
</div>
